import axios, { AxiosRequestConfig } from 'axios';
import { adminAuthHeaders } from './adminAuthHeaders';

const getAuthHeaders = adminAuthHeaders;

const API_BASE = import.meta.env.VITE_API_URL || 'https://admin.clarityofconcept.com/api';

interface MediaListParams {
  page?: number;
  limit?: number;
  folder_id?: number | null;
  category?: string;
  search?: string;
}

interface MediaListResponse {
  items: MediaItem[];
  total: number;
  page: number;
  pages: number;
  limit: number;
}

interface MediaItem {
  id: number;
  uuid: string;
  filename: string;
  title: string;
  caption?: string;
  alt_text?: string;
  category: string;
  folder_id?: number;
  tags?: string[];
  original_path: string;
  optimized_path?: string;
  thumbnail_path?: string;
  width: number;
  height: number;
  original_size: number;
  optimized_size?: number;
  uploaded_at: string;
  usage_count: number;
  mime_type?: string;
}

interface Folder {
  id: number;
  name: string;
  slug: string;
  parent_id?: number;
  path: string;
  color?: string;
  icon?: string;
  item_count: number;
}

interface MediaUpdateData {
  title?: string;
  caption?: string;
  alt_text?: string;
  tags?: string[];
  category?: string;
  folder_id?: number | null;
}

export const mediaApi = {
  // List media items with filtering
  async list(params: MediaListParams = {}): Promise<MediaListResponse> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.folder_id !== undefined && params.folder_id !== null) {
      queryParams.append('folder_id', params.folder_id.toString());
    }
    if (params.category) queryParams.append('category', params.category);
    if (params.search) queryParams.append('search', params.search);

    const response = await axios.get(
      `${API_BASE}/media?${queryParams.toString()}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  // Upload media file
  async upload(formData: FormData, config?: AxiosRequestConfig): Promise<MediaItem> {
    const response = await axios.post(
      `${API_BASE}/media/upload`,
      formData,
      {
        headers: {
          ...getAuthHeaders()
        },
        ...config
      }
    );
    return response.data;
  },

  // Get single media item
  async get(id: number): Promise<MediaItem> {
    const response = await axios.get(
      `${API_BASE}/media/${id}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  // Update media metadata
  async update(id: number, data: MediaUpdateData): Promise<MediaItem> {
    const url = `${API_BASE}/media/${id}`;
    const headers = getAuthHeaders();

    try {
      const response = await axios.patch(url, data, { headers });
      return response.data;
    } catch (error: any) {
      console.error('MediaAPI Update - Error:', error.response || error);
      throw error;
    }
  },

  // Delete media item
  async delete(id: number): Promise<void> {
    await axios.delete(
      `${API_BASE}/media/${id}`,
      { headers: getAuthHeaders() }
    );
  },

  // Bulk delete media items
  async bulkDelete(ids: number[]): Promise<void> {
    await axios.post(
      `${API_BASE}/media/bulk/delete`,
      { media_ids: ids },
      { headers: getAuthHeaders() }
    );
  },

  // Bulk move media items to folder
  async bulkMove(ids: number[], folderId: number | null): Promise<void> {
    await axios.post(
      `${API_BASE}/media/bulk/move`,
      { media_ids: ids, folder_id: folderId },
      { headers: getAuthHeaders() }
    );
  },

  // Get folders
  async getFolders(): Promise<Folder[]> {
    const response = await axios.get(
      `${API_BASE}/media/folders`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  // Create folder
  async createFolder(data: { name: string; parent_id?: number }): Promise<Folder> {
    const formData = new FormData();
    formData.append('name', data.name);
    if (data.parent_id !== undefined) formData.append('parent_id', data.parent_id.toString());

    const response = await axios.post(
      `${API_BASE}/media/folders`,
      formData,
      {
        headers: {
          ...getAuthHeaders()
        }
      }
    );
    return response.data;
  },

  // Update folder
  async updateFolder(id: number, data: { name?: string; color?: string; icon?: string }): Promise<Folder> {
    const response = await axios.patch(
      `${API_BASE}/media/folders/${id}`,
      data,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  // Delete folder
  async deleteFolder(id: number): Promise<void> {
    await axios.delete(
      `${API_BASE}/media/folders/${id}`,
      { headers: getAuthHeaders() }
    );
  },

  // Search media
  async search(query: string, category?: string): Promise<MediaItem[]> {
    const params = new URLSearchParams({ q: query });
    if (category) params.append('category', category);

    const response = await axios.get(
      `${API_BASE}/media/search?${params.toString()}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  // Get media analytics
  async getAnalytics(mediaId: number): Promise<any> {
    const response = await axios.get(
      `${API_BASE}/media/${mediaId}/analytics`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  // Track media usage
  async trackUsage(mediaId: number, context: string): Promise<void> {
    await axios.post(
      `${API_BASE}/media/${mediaId}/track`,
      { context },
      { headers: getAuthHeaders() }
    );
  },

  // Create folder template
  async createFolderTemplate(templateName: string): Promise<any> {
    const formData = new FormData();
    formData.append('template_name', templateName);

    const response = await axios.post(
      `${API_BASE}/media/folders/templates`,
      formData,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  // Bulk upload
  async bulkUpload(files: File[], category?: string, folderId?: number): Promise<any> {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    if (category) formData.append('category', category);
    if (folderId) formData.append('folder_id', folderId.toString());

    const response = await axios.post(
      `${API_BASE}/media/bulk-upload`,
      formData,
      {
        headers: {
          ...getAuthHeaders()
        }
      }
    );
    return response.data;
  },


  // Get analytics summary
  async getAnalyticsSummary(): Promise<any> {
    const response = await axios.get(
      `${API_BASE}/media/analytics/summary`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }
};
